import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { passwordSchema } from "@opendialysis/procare-api-schemas";
import { setPasswordWithToken, verifyPasswordToken } from "../services/authService";
import {
  errorMessageKey,
  normaliseError,
  type ErrorMessageKey,
} from "../../../lib/apiError";
import { PasswordField } from "../../../components/PasswordField";
import { PasswordRequirements } from "../../../components/PasswordRequirements";

type FormValues = { password: string; confirmPassword: string };

/**
 * Both emailed links are single-use and both are spent by the same column, so
 * an activation link stops working the moment the account is activated. That
 * was already enforced server-side — but the page offered the form anyway and
 * only said so after the person had chosen a password and typed it twice.
 * The link is now checked before anything is rendered.
 */
type LinkState =
  | { status: "checking" }
  | { status: "usable" }
  | {
      status: "unusable";
      messageKey: ErrorMessageKey | "auth:setPassword.invalidLink";
    };

/**
 * Serves BOTH emailed links, which are built server-side and must be honoured
 * verbatim:
 *   ${DASHBOARD_BASE_URL}/activate?token=…&userId=…&firstName=…&lastName=…
 *   ${DASHBOARD_BASE_URL}/reset-password?token=…&userId=…
 */
export default function SetPasswordPage({
  mode,
}: {
  mode: "activate" | "reset";
}) {
  const { t } = useTranslation(["auth", "errors"]);
  const [params] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  // Confirm in place instead of bouncing to /signin with a notice. Landing on
  // a login form is not feedback: the person reads it as the next page, not as
  // the answer to what they just did — and they arrive at their own pace here.
  const [done, setDone] = useState(false);
  // ONE toggle for both fields. The password being typed here is a new one the
  // person has never seen, which is exactly why the confirm field exists —
  // revealing only half of the pair would leave that check meaningless.
  const [showPassword, setShowPassword] = useState(false);

  const userId = params.get("userId");
  const token = params.get("token");
  // Display-only, and untrusted: it arrives in a URL anyone can edit.
  const firstName = params.get("firstName")?.slice(0, 60) ?? "";

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { password: "", confirmPassword: "" },
  });

  const [link, setLink] = useState<LinkState>(() =>
    userId && token
      ? { status: "checking" }
      : { status: "unusable", messageKey: "auth:setPassword.invalidLink" },
  );

  useEffect(() => {
    if (!userId || !token) return;
    // Guards the unmount race only; the check spends nothing, so React 19's
    // double-invoked effect just asks twice.
    let current = true;
    verifyPasswordToken({ userId, token })
      .then(() => {
        if (current) setLink({ status: "usable" });
      })
      .catch((error) => {
        if (!current) return;
        const failure = normaliseError(error);
        // A CODED refusal is the server saying this link is dead, and the
        // only five it can answer here all mean exactly that. Everything else
        // — a network failure, the rate limit, a misconfigured X-Api-Key —
        // says nothing about the token, so it falls through to the form
        // rather than stranding someone whose link is perfectly good. That
        // fallback is precisely how this page behaved before the check
        // existed, which is what makes the check safe to add.
        setLink(
          failure.code
            ? { status: "unusable", messageKey: errorMessageKey(failure) }
            : { status: "usable" },
        );
      });
    return () => {
      current = false;
    };
  }, [userId, token]);

  const onSubmit = handleSubmit(async (values) => {
    if (!userId || !token) return;
    setFormError(null);
    try {
      await setPasswordWithToken({
        userId,
        token,
        newPassword: values.password,
      });
      setDone(true);
    } catch (error) {
      setFormError(t(errorMessageKey(normaliseError(error))));
    }
  });

  return (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} component="form" onSubmit={onSubmit} noValidate>
          <Typography variant="h2" component="h1">
            {done
              ? t(
                  mode === "activate"
                    ? "auth:setPassword.doneTitleActivate"
                    : "auth:setPassword.doneTitleReset",
                )
              : link.status === "unusable"
                ? t("auth:setPassword.invalidTitle")
                : t(
                    mode === "activate"
                      ? "auth:setPassword.activateTitle"
                      : "auth:setPassword.resetTitle",
                  )}
          </Typography>

          {done ? (
            <>
              <Alert severity="success">
                {t(
                  mode === "activate"
                    ? "auth:setPassword.successActivate"
                    : "auth:setPassword.successReset",
                )}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t("auth:setPassword.doneHint")}
              </Typography>
              <Box>
                <Button
                  component={Link}
                  to="/signin"
                  variant="contained"
                  size="large"
                  sx={{ borderRadius: 8, px: 5 }}
                >
                  {t("auth:setPassword.goToSignIn")}
                </Button>
              </Box>
            </>
          ) : link.status === "checking" ? (
            // Nothing for the first 200ms, like SplashScreen: one round trip
            // usually resolves well inside that, and a spinner that appears
            // and vanishes reads as a glitch where a blank reads as instant.
            <Fade in style={{ transitionDelay: "200ms" }}>
              <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
                <CircularProgress
                  size={22}
                  thickness={5}
                  aria-label={t("auth:setPassword.checkingLink")}
                />
              </Box>
            </Fade>
          ) : link.status === "unusable" ? (
            <>
              <Alert severity="error">{t(link.messageKey)}</Alert>
              <Typography variant="body2" color="text.secondary">
                {t("auth:setPassword.invalidHint")}
              </Typography>
              <Stack
                direction="row"
                spacing={3}
                sx={{ flexWrap: "wrap", rowGap: 1 }}
              >
                {/* Sign-in first: the commonest dead link is an activation
                    one clicked twice, and that account already works. */}
                <MuiLink component={Link} to="/signin" variant="body2">
                  {t("auth:setPassword.goToSignIn")}
                </MuiLink>
                <MuiLink component={Link} to="/forgot-password" variant="body2">
                  {t("auth:forgot.title")}
                </MuiLink>
              </Stack>
            </>
          ) : (
            <>
              {firstName ? (
                <Typography variant="body2" color="text.secondary">
                  {t("auth:setPassword.greeting", { name: firstName })}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {t(
                  mode === "activate"
                    ? "auth:setPassword.activateIntro"
                    : "auth:setPassword.resetIntro",
                )}
              </Typography>

              {formError ? <Alert severity="error">{formError}</Alert> : null}

              <PasswordField
                {...register("password", {
                  // The policy lives in the shared contract; the checklist
                  // below and the API read the same rules.
                  validate: (value) =>
                    passwordSchema.safeParse(value).success ||
                    t("auth:setPassword.tooWeak"),
                })}
                label={t("auth:setPassword.password")}
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                shown={showPassword}
                onToggle={() => setShowPassword((value) => !value)}
                showLabel={t("auth:setPassword.showPasswords")}
                hideLabel={t("auth:setPassword.hidePasswords")}
              />

              <PasswordField
                {...register("confirmPassword", {
                  validate: (value) =>
                    value === watch("password") ||
                    t("auth:setPassword.mismatch"),
                })}
                label={t("auth:setPassword.confirm")}
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.confirmPassword)}
                helperText={errors.confirmPassword?.message}
                // Follows the toggle above — no second icon for one state.
                shown={showPassword}
              />

              <PasswordRequirements value={watch("password")} />

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ borderRadius: 8, px: 5 }}
                >
                  {t("auth:setPassword.submit")}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
